import { Component, OnInit, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { BreakpointObserver } from "@angular/cdk/layout";
import { PageEvent } from "@angular/material/paginator";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  SimpleDialogService,
  CommonActionKey,
  LoanDocumentService,
  LoanFileTemplateData,
  MessageService,
  FilterParameter,
} from "common";
import { QueryParams } from "common";
import {
  DocumentListBaseComponent,
  DocumentListComponentData,
} from "./document-list-base.component";
import { LoanFacade } from "../../loan/domain/+state/loan.facade";
import { LoanData } from "../../loan/domain/models/loan.model";
import { LoanDocumentFilterService } from "../document-filter.service";
import { RequestStatementDialogComponent } from "../request-statement-dialog/request-statement-dialog.component";

// Must equal the server's StatementTemplateGroup — templates are matched by group, not name.
const STATEMENT_TEMPLATE_GROUP = "TRX_HIST_STAT";

@UntilDestroy()
@Component({
    selector: "ifp-account-document-list",
    templateUrl: "./document-list.component.html",
    styleUrls: ["./document-list.component.scss"],
    standalone: false
})
export class AccountDocumentListComponent
  extends DocumentListBaseComponent<LoanDocumentService>
  implements OnInit, OnDestroy
{
  private loanId: number;
  private statementTemplateId?: number;
  private accountOpenedOn?: Date;

  constructor(
    private readonly router: Router,
    private readonly loanFacade: LoanFacade,
    route: ActivatedRoute,
    _documentService: LoanDocumentService,
    _documentFilterService: LoanDocumentFilterService,
    dialog: MatDialog,
    breakpointObserver: BreakpointObserver,
    messageService: MessageService,
    private readonly simpleDialogService: SimpleDialogService
  ) {
    super(
      route,
      _documentService,
      _documentFilterService,
      dialog,
      breakpointObserver,
      messageService
    );
  }

  get pageSize(): number {
    return this._documentService.pageSize;
  }
  get queryParams(): QueryParams {
    return this._documentService.queryParams;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: AccountDocumentListComponentData) => {
        this.data = data;
        this.loanId = data?.loan?.id;
        this.accountOpenedOn = data?.loan?.chainOriginatedOn ?? data?.loan?.createdOn;
        this.statementTemplateId = data?.loanFileTemplates?.find(
          (template) => template.group?.toUpperCase() === STATEMENT_TEMPLATE_GROUP
        )?.id;
      });
  }

  override get canRequestStatement(): boolean {
    return this.statementTemplateId != null;
  }

  override requestStatement(): void {
    if (this.statementTemplateId == null)
      return;
    RequestStatementDialogComponent.show(this.dialog, {
      loanId: this.loanId,
      templateId: this.statementTemplateId,
      accountOpenedOn: this.accountOpenedOn,
    })
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  ngOnDestroy(): void {
    this._documentService.queryParams = {};
  }

  activeFilterRemoved(activefilters: FilterParameter[]): void {
    if (!activefilters.length) return;
    activefilters.forEach((item) => {
      this.filters.remove(item.id);
      this.filter();
    });
  }

  setPage(event: PageEvent): void {
    this._documentService.pageSize = event.pageSize;
    this.filter();
  }

  filter(): void {
    const query = this._documentFilterService.filter(
      this.filters,
      this.paginator,
      this.sort
    );
    this._documentService.query(this.loanId, query).subscribe((result) => {
      this.data.documentQueryResult = result;
      this.activeFilters = this._documentFilterService.addFilterParameters(
        this.queryParams
      );
    });
  }

  download(id: number, loanId: number): void {
    this._documentService.download(id, loanId);
  }

  get canUpload(): boolean {
    return false;
  }

  upload(): void {
    this.router.navigate([`account/${this.loanId}/documents/upload`]);
  }

  delete(id: number, filename: string): void {
    this.simpleDialogService.openSimpleConfirmDialog(`Delete file ${filename}?`, 'Confirm')
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this._documentService.delete(id, this.loanId)
            .subscribe({
              next: this.deleteSuccessHandler.bind(this),
              error: this.deleteErrorHandler.bind(this),
            });
        }
      });
  }

  deleteSuccessHandler(): void {
    this.deleteSubClear();
    this.loanFacade.refreshLoan(this.loanId).subscribe();
  }
}

export interface AccountDocumentListComponentData extends DocumentListComponentData {
  loan: LoanData;
  loanFileTemplates?: LoanFileTemplateData[];
}
